import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import moment, { Moment } from "moment";
import { dateFormat, dbDateFormat } from "../constants/constants";
export const formatDate = (
  date?: string | Date,
  format: string = dateFormat,
) => {
  return moment(date || new Date()).format(format);
};

export const formatTime = (date?: string | Date, format: string = "h:mm A") => {
  return moment(date || new Date()).format(format);
};
export const formatMessageTime = (
  date?: string | Date,
  format: string = "h:mm A",
) => {
  const now = moment();
  const inputTime = moment(date || new Date());
  const diffInMinutes = now.diff(inputTime, "minutes");
  const diffInHours = now.diff(inputTime, "hours");

  if (diffInMinutes < 1) {
    return "just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 6) {
    return `${diffInHours}h ago`;
  } else {
    return inputTime.format(format);
  }
};
export const formatOnlyTime = (
  time: string,
  defaultFormat: string = "h:mm:ss",
  format: string = "h:mm A",
) => {
  return moment(time, defaultFormat).format(format);
};

export const convertMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${remainingMinutes.toString().padStart(2, "0")}`;
};

export function convertMinutesToHoursWithText(minutes: number): string {
  const duration = moment.duration(minutes, "minutes");
  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();

  return !minutes
    ? "0 min"
    : `${
        hours
          ? `${hours} hr${hours !== 1 ? "s" : ""} ${mins ? "and" : ""} `
          : ""
      }${mins ? `${mins} min` : ""} `;
}

export function convertMinutesToHoursShort(minutes: number): string {
  if (!minutes || isNaN(minutes)) return "0 hr 0 min 0 sec";

  const duration = moment.duration(minutes, "minutes");
  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();
  const secs = duration.seconds();

  return `${hours} hr ${mins} min ${secs} sec`;
}

export function convertSecondsToHoursShort(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "0 hr 0 min 0 sec";

  const duration = moment.duration(seconds, "seconds");
  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();
  const secs = duration.seconds();

  return `${hours} hr ${mins} min ${secs} sec`;
}

export function convertTimeToMinutes(time: string): number {
  if (!time) return 0;
  const [hoursStr, minutesStr] = time?.split(":");

  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error("Invalid time format. Expected 'HH:mm'.");
  }

  return hours * 60 + minutes;
}
// calculate just minutes
export const calculateMinutes = (startTime: string, endTime: string) => {
  const start = moment(startTime, "h:mm A");
  const end = moment(endTime, "h:mm A");

  return end.diff(start, "minutes");
};

export const getTimeDurationInDays = (
  startDate: string,
  endDate: string,
): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const diffInMilliseconds = end.getTime() - start.getTime();
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

  return Math.round(diffInDays);
};

export const formatMinutesToHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const formatSecondToMinutes = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export const isExpired = (expirationDate: string, format = dbDateFormat) => {
  const currentDate = moment();
  const date = moment(expirationDate).format(format);
  return moment(date, format).isBefore(currentDate);
};

export const disableTodayAndFutureDates = (current: any) => {
  return current && current >= moment().startOf("day");
};

export const disableFutureDates = (current: any) => {
  return current && current > moment().endOf("day");
};

export const disablePastDates = (current: any) => {
  return current && current < moment().startOf("day");
};

export const getLastTenYears = (): string[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) =>
    (currentYear - index).toString(),
  );
};

export const convertMinutesToTime = (minutes: number = 0) => {
  const duration = moment.duration(minutes, "minutes");

  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();

  return `${hours}:${mins.toString().padStart(2, "0")}`;
};

export const formatUTCTime = (date: string | Moment) => {
  return moment.utc(date).local().toISOString();
};
export const formatUTCDate = (
  date: string | Moment,
  format = "DD MMM, YYYY",
) => {
  return moment.utc(date).format(format);
};
export const UTCTimeFormat = (date: string | Moment, format = "hh:mm A") => {
  return moment.utc(date).format(format);
};

export const getStartAndEndDateInAMonth = (
  date: Date,
  format = dbDateFormat,
) => {
  return {
    startDate: moment(date).startOf("month").format(format),
    endDate: moment(date).endOf("month").format(format),
  };
};

export function getMinutesDiff(
  date1: string | Date,
  date2: string | Date,
): number {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffInMs = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffInMs / 1000 / 60);
}

export function getSecondsDiff(
  date1: string | Date,
  date2: string | Date,
): number {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffInMs = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffInMs / 1000);
}

export function convertMinutesToSeconds(minutes: number): number {
  if (!minutes || minutes < 0) return 0;
  return minutes * 60;
}

// extend once before using anywhere
dayjs.extend(utc);
dayjs.extend(timezone);
export const formatTimeBD = (dateString: string) => {
  if (!dateString) return "N/A";
  return dayjs.utc(dateString).tz("Asia/Dhaka").format("hh:mm A"); // or 'HH:mm' for 24hr
};

// Extracted helper for consistent date formatting
export const formatDateTime = (date?: string) => {
  return date ? `${formatTime(date)}, ${formatDate(date)}` : "";
};
